import React, {useEffect, useMemo, useState} from 'react';
import {useTheme} from "@mui/material/styles";
import {useQueryClient} from "@tanstack/react-query";
import {MaterialReactTable, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import {Avatar, Box, Grid} from "@mui/material";
import MuiReadOnlyBoxBody from "../../../../../ui/mui/mui-display-data/MuiReadOnlyBoxBody";
import Loading from "../../../../../ui/Loading";
import MuiFabButtonBack from "../../../../../ui/mui/mui_buttons/fab/extended/MuiFabButtonBack";
import MuiFetchingSpinner from "../../../../../ui/mui/MuiFetchingSpinner";
import useApiGetRaspberryPiIps from "./api-hooks/useApiGetRaspberryPiIps";
import useCustomCellRenderer from "./useCustomCellRenderer";
import {IRaspberryPiIp} from "../../../../../../utils/interfaces/industry_machine/RaspberryPiPinLog/IRaspberryPiIp";
import {
    getMuiTableBodyCellProps,
    getMuiTableBodyRowProps,
    getMuiTableHeadCellProps,
    getMuiTablePaperProps
} from "../../../../../ui/mui/mui_react_table/MuiReactTableStyles";
import {FaRaspberryPi} from "react-icons/fa";
import MuiBorderedBox from "../../../../../ui/mui/MuiBorderedBox";
import RaspberryPiPinTable from "./raspberry_pi_pin/RaspberryPiPinTable";

interface Props {
    onSelect?: (obj: IRaspberryPiIp | undefined) => void,
    onClose?: () => void;
    alreadySelectedObj?: IRaspberryPiIp | undefined,
}


const RaspberryPiIpTable = ({onSelect, onClose, alreadySelectedObj}: Props) => {
    const theme = useTheme();
    const queryClient = useQueryClient();

    const [selectedRow, setSelectedRow] = useState<IRaspberryPiIp | undefined>()
    const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false)
    const [rows, setRows] = useState<IRaspberryPiIp[] | undefined>()


    const {
        data,
        isLoading,
        error,
        isFetching,
    } = useApiGetRaspberryPiIps();


    useEffect(() => {
        if (data) {
            setRows(data);
        }
    }, [data]);

    const CustomCellRenderer: React.FC<{ row: IRaspberryPiIp, theme: any }> = ({row, theme}) => {
        const {renderIcons} = useCustomCellRenderer(row, theme);
        return renderIcons();
    };


    const columns = useMemo<MRT_ColumnDef<IRaspberryPiIp>[]>(
        () => [
            {
                id: "icons",
                header: "",
                columnDefType: "display",
                Cell: ({row}) => (<CustomCellRenderer row={row.original} theme={theme}/>)
            },

            {
                accessorKey: 'raspberry_pi_ip',
                header: ('ip'),
                filterFn: 'contains',
            },
        ],
        [rows]
    );


    const table = useMaterialReactTable<IRaspberryPiIp>({
        columns,
        defaultColumn: {
            maxSize: 400,
            minSize: 80,
            size: 80,
        },
        data: rows ? rows : [],
        columnFilterDisplayMode: 'popover',
        globalFilterFn: 'contains',
        enableFullScreenToggle: false,
        enableFilters: true,
        enableDensityToggle: true,
        muiTableHeadCellProps: getMuiTableHeadCellProps(theme.palette.primary.main),
        muiTableBodyCellProps: getMuiTableBodyCellProps(theme),
        muiTablePaperProps: getMuiTablePaperProps,
        enablePagination: rows && rows.length > 10,
        initialState: {
            density: 'compact',
            pagination: {pageSize: 25, pageIndex: 0},
        },
        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.backgroundAppGarageColor.main,

        }),
        muiTableBodyRowProps: getMuiTableBodyRowProps(theme),
        renderTopToolbarCustomActions: ({table}) => (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <>
                    <Avatar sx={{
                        backgroundColor: 'white',
                        border: `2px solid ${theme.palette.primary.main}`,
                        marginRight: 1
                    }}>
                        <FaRaspberryPi color={theme.palette.primary.main}/>
                    </Avatar>
                    <MuiReadOnlyBoxBody label={''} value={'IP'}/>
                </>
            </Box>),
        enableExpandAll: false,
        displayColumnDefOptions: {
            'mrt-row-expand': {
                header: ('pin'),
            },
        },
        muiDetailPanelProps: () => ({
            sx: (theme) => ({
                backgroundColor: theme.palette.backgroundAppGarageColor.dark
            }),
        }),
        muiExpandButtonProps: ({row, table}) => ({
            onClick: () => table.setExpanded({[row.id]: !row.getIsExpanded()}),
            sx: {
                transform: row.getIsExpanded() ? 'rotate(180deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s',
                color: theme.palette.primary.main,
            },
        }),
        renderDetailPanel: ({row}) =>
            (
                <MuiBorderedBox>
                    <RaspberryPiPinTable ip={row.original.raspberry_pi_ip}/>
                </MuiBorderedBox>
            )
    });


    return <>
        {isLoading ? <Loading/> :
            <>
                {onClose &&
                    <Grid item xs={12} style={{textAlign: 'center'}}>
                        <Box className={'mt-2 gap-x-2 mb-2'}>
                            <MuiFabButtonBack onClick={onClose}/>
                        </Box>
                    </Grid>}

                <MaterialReactTable table={table}/>


                {isFetching &&
                    <MuiFetchingSpinner text="Fetching data..."/>
                }
            </>}
    </>;
};

export default RaspberryPiIpTable;
